import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Tabs, Tab, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import TableRenderer from '../TableRenderer';
import { useEntityNames, entityDisplayName } from '../../services/EntityNameService';
import OrderableListPanel from './OrderableListPanel';
import EntityWizardOverlay from './EntityWizardOverlay';
import { useColumnVisibility, applyColumnVisibility } from './fieldVisibility';
import FilterChipBar from './FilterChipBar';
import CriteriaBuilderPanel from './CriteriaBuilderPanel';
import CountEntryPanel from './CountEntryPanel';
import AddCustomFieldDialog from './AddCustomFieldDialog';
import PhotosDialog from './PhotosDialog';
import WizardRunPanel from './WizardRunPanel';
import AccordionDetail from './AccordionDetail';
import WorkspaceLayoutView from './WorkspaceLayoutView';
import HelpIcon from './HelpIcon';
import WorkspaceToolbar from './WorkspaceToolbar';
import WorkspaceStatusBar from './WorkspaceStatusBar';
import WorkspaceFormOverlay from './WorkspaceFormOverlay';
import WorkspacePickerOverlay from './WorkspacePickerOverlay';
import WorkspaceConfirmDialog from './WorkspaceConfirmDialog';
import BulkConfirmOverlay from './BulkConfirmOverlay';
import RapidEntryOverlay from './RapidEntryOverlay';
import ColumnConfigDialog from '../ColumnConfigDialog';
import useWorkspaceData from './useWorkspaceData';
import useWorkspaceActions from './useWorkspaceActions';
import {
  normalizeView, buildTableData, buildDetailTableData, buildColumns, rowsToCsv, downloadTextFile,
  liftDefaultChipsForSearch, defaultChipFilters, savedMessage,
  resolveSmartSearchFieldMeta,
} from './workspaceUtils';
import ChatService from '../../services/ChatService';
import useHiddenRowNotice, { HiddenRowNotice } from './useHiddenRowNotice';
import { componentRegistry } from './registry';
import { useAuth } from '../../contexts/AuthContext';

/**
 * MetadataWorkspace - Generic workspace renderer driven entirely by JSON metadata.
 *
 * Supports v2 JSON schema (content[] array with entity refs and parentParams binding)
 * and legacy schema (master/detail).
 * Data is loaded via loadScript (named FLEX script reference).
 * Columns are explicitly declared in the columns array — nothing auto-discovered.
 */
const MetadataWorkspace = ({ definition, colors, onError, navContext, onOpenWorkspace, workspaceCode, isActive }) => {
  useEntityNames();
  const { user: currentUser } = useAuth();
  const def = typeof definition === 'string' ? JSON.parse(definition) : definition;
  // CUSTOM_COMPONENT workspaces (definition.componentType === 'CUSTOM_COMPONENT')
  // bypass the entire views/grid machinery below and render an arbitrary React
  // component (registered via componentRegistry) instead -- for a workspace
  // that isn't a data grid at all (e.g. User Admin). The below hooks still all
  // run unconditionally either way (rules of hooks); only the final render
  // branches, right before the main `return` at the bottom of this component.
  const isCustomComponent = def.componentType === 'CUSTOM_COMPONENT';
  const CustomComponent = isCustomComponent ? componentRegistry.resolve(def.componentCode) : null;
  const rawViews = def.views || [];
  const columnVisibleByScript = useColumnVisibility(def.views);
  // allViews includes "hidden": true views (normalized) so a NAVIGATE action
  // (e.g. a row action's targetView) can still resolve and render one as a
  // drill-down -- Zones' "View Members" -> zone-members is exactly this
  // pattern. `views` is the tab-bar-visible subset: hidden views are excluded
  // from the tab bar and can never become the default/active view, but they
  // ARE still reachable once activeViewId is set explicitly.
  const allViews = rawViews.map(normalizeView);
  const views = allViews.filter((v) => v.hidden !== true);

  const defaultView = views.find((v) => v.default) || views[0];
  const [activeViewId, setActiveViewId] = useState(navContext?.viewId || defaultView?.viewId || '');

  // Bumped on "Reset" for a form view -- forces EntityWizardOverlay to remount
  // with freshly-reloaded rowData, discarding any in-progress edits, instead
  // of relying on it noticing a prop change after it's already mounted.
  const [formResetNonce, setFormResetNonce] = useState(0);

  // Data hook
  const data = useWorkspaceData(allViews, activeViewId, setActiveViewId);
  const {
    activeView, masterData, masterLoading, masterError, expandedRowId, setExpandedRowId,
    detailData, detailLoading, activeDetailTab, setActiveDetailTab,
    activeFilters, setActiveFilters, navParams, pendingNavParamsRef,
    fieldMetaCache, viewToolbar, viewFilters, viewActions,
    resolvedDetails, loadMasterData, loadDetailData, exportMasterData,
    statusCounters, advancedSearchFieldMeta,
  } = data;

  // Universal "Download data" toolbar action -- every grid view gets this for
  // free (see WorkspaceToolbar), no per-view opt-in. Re-fetches the full
  // filtered result set (exportMasterData, not the possibly-paginated
  // masterData already in state) and reuses the exact same column
  // set/order/labels the grid itself is showing, so the CSV always matches
  // what's on screen.
  const handleExport = useCallback(async () => {
    if (!activeView?.master?.loadScript) return;
    try {
      const [rows, columns] = await Promise.all([
        exportMasterData(),
        Promise.resolve(buildColumns(activeView.master, fieldMetaCache)),
      ]);
      const csv = rowsToCsv(columns, rows);
      const safeName = (activeView.label || activeView.viewId || 'export').replace(/[^a-z0-9]+/gi, '_');
      downloadTextFile(`${safeName}.csv`, csv);
    } catch (err) {
      console.error('Export failed:', err);
    }
  }, [activeView, fieldMetaCache, exportMasterData]);

  // "Configure table columns" settings icon — shared by the master grid, detail
  // accordions, and layout-view content blocks (all render via TableRenderer).
  const [columnConfigDialog, setColumnConfigDialog] = useState({ open: false, entityType: null });
  const handleConfigureTable = useCallback((tableId, entityType) => {
    if (!entityType) {
      console.warn('Table configure: no entityType on table, cannot load column metadata for', tableId);
      return;
    }
    setColumnConfigDialog({ open: true, entityType });
  }, []);

  // Saved column visibility/order per entity, lazily fetched and cached. A ref
  // (not state) holds the cache itself so applyColumnPrefs can read/write it
  // synchronously during render without triggering extra renders; columnPrefsVersion
  // is the state bump that tells buildTable/buildDetailTable to recompute once a
  // fetch resolves (or a save updates the cache directly).
  const columnPrefsCacheRef = useRef({});
  const [columnPrefsVersion, setColumnPrefsVersion] = useState(0);
  const applyColumnPrefs = useCallback((columns, entityType) => {
    if (!entityType || !Array.isArray(columns)) return columns;
    const cached = columnPrefsCacheRef.current[entityType];
    if (cached === undefined) {
      columnPrefsCacheRef.current[entityType] = null; // mark as fetching to avoid duplicate requests
      ChatService.getColumnConfig(entityType).then((prefs) => {
        columnPrefsCacheRef.current[entityType] = prefs || [];
        setColumnPrefsVersion((v) => v + 1);
      });
      return columns;
    }
    if (!cached || cached.length === 0) return columns;

    const byId = new Map(columns.map((c) => [c.columnId, c]));
    const ordered = [];
    cached.forEach((p) => {
      const col = byId.get(p.fieldName);
      if (col) {
        byId.delete(p.fieldName); // remove regardless of visibility, so hidden columns don't fall through below
        if (p.visible !== false) ordered.push(col);
      }
    });
    byId.forEach((c) => ordered.push(c)); // columns added since the user last configured this grid
    return ordered.length > 0 ? ordered : columns;
  }, []);

  // Action result display
  const [actionResult, setActionResult] = useState(null);
  const [actionResultData, setActionResultData] = useState(null);
  const [errorSnack, setErrorSnack] = useState(null);
  const handleActionSuccess = useCallback((msg, fullResult) => {
    // _showResult: the result must be read (e.g. a key shown once, FH-382), never just a passing toast
    if ((msg && msg.length > 50) || fullResult?._showResult === true) {
      setActionResult(msg);
      setActionResultData(fullResult || null);
    } else {
      setErrorSnack({ severity: 'success', message: msg || 'Action completed.' });
    }
  }, []);
  const handleActionError = useCallback((msg) => {
    setErrorSnack({ severity: 'error', message: msg || 'Action failed.' });
    if (onError) onError(msg);
  }, [onError]);

  // After an edit/action: refresh master grid in place, and layout-view blocks via a token
  const [layoutRefresh, setLayoutRefresh] = useState(0);
  const reloadData = useCallback((opts) => {
    loadMasterData(opts);
    setLayoutRefresh((n) => n + 1);
  }, [loadMasterData]);

  // Tabs stay mounted while hidden, so a row action that opened another workspace (Put Away,
  // Plan Order, ...) left this grid showing what it had before. Refresh in place on the way back.
  const wasActiveRef = useRef(isActive);
  useEffect(() => {
    if (isActive && wasActiveRef.current === false) reloadData();
    wasActiveRef.current = isActive;
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actions hook
  const actions = useWorkspaceActions({
    activeView, resolvedDetails, loadMasterData: reloadData, onError: handleActionError, onSuccess: handleActionSuccess, pendingNavParamsRef, setActiveViewId, navParams, onOpenWorkspace,
  });

  // Expanded row data for conditional detail rendering
  const [expandedRowData, setExpandedRowData] = useState(null);

  // Splitter state for master/detail resize
  const [splitPercent, setSplitPercent] = useState(50);
  const splitContainerRef = useRef(null);

  const handleSplitDragStart = useCallback((e) => {
    e.preventDefault();
    const container = e.target.closest('[style*="flex-direction: column"]') || e.target.parentElement;
    const startY = e.clientY;
    const startPercent = splitPercent;
    const rect = container.getBoundingClientRect();

    const onMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const deltaPercent = (deltaY / rect.height) * 100;
      const newPercent = Math.min(85, Math.max(15, startPercent + deltaPercent));
      setSplitPercent(newPercent);
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [splitPercent]);

  // Warn before browser refresh/close if an overlay is open
  const hasUnsavedWork = actions.formOpen || actions.overlayMode || actions.criteriaBuilderOpen || actions.countEntryOpen || actions.pickerOpen;
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedWork]);

  // Handle cross-workspace navigation context
  useEffect(() => {
    if (!navContext) return;
    if (navContext.viewId) setActiveViewId(navContext.viewId);
    if (navContext.search) {
      data.setSearchQuery(navContext.search);
      setPresetSearch({ text: navContext.search });
      navSearchRef.current = { viewId: navContext.viewId || activeViewId, text: navContext.search };
    }
    if (navContext.filters) {
      // a link opens the screen on its record: a search left from an earlier visit would narrow it further (FH-228)
      if (!navContext.search) {
        data.setSearchQuery('');
        setPresetSearch({ text: '' });
      }
      if (navContext.viewId && navContext.viewId !== activeViewId) {
        // the view change resets the filters; it applies these instead
        pendingNavParamsRef.current = navContext.filters;
      } else {
        data.setAdvancedSearchParams(navContext.filters);
        // shown as chips in the search box, so the screen says what it is filtered to and each can be removed
        data.setNavParams(navContext.filters);
      }
    }
  }, [navContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Search handlers
  // A search looks across every status: the view's default chips (its starting view) are lifted
  // while searching and come back when the search is cleared (FH-203)
  const defaultsLiftedRef = useRef(false);
  const handleStructuredSearch = useCallback((params) => {
    const { q, ...fieldParams } = params;
    data.setSearchQuery(q || '');
    data.setAdvancedSearchParams(fieldParams);
    if ((q || '').trim() || Object.keys(fieldParams).length > 0) {
      data.setActiveFilters((prev) => {
        const { filters, lifted } = liftDefaultChipsForSearch(prev, viewFilters?.chips);
        if (lifted) defaultsLiftedRef.current = true;
        return filters;
      });
    } else if (defaultsLiftedRef.current) {
      // an emptied search is a cleared one
      defaultsLiftedRef.current = false;
      data.setActiveFilters((prev) => (prev.length === 0 ? defaultChipFilters(viewFilters?.chips) : prev));
    }
  }, [viewFilters]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleStructuredSearchClear = useCallback(() => {
    data.setSearchQuery('');
    data.setAdvancedSearchParams({});
    if (defaultsLiftedRef.current) {
      defaultsLiftedRef.current = false;
      data.setActiveFilters((prev) => (prev.length === 0 ? defaultChipFilters(viewFilters?.chips) : prev));
    }
  }, [viewFilters]);  // eslint-disable-line react-hooks/exhaustive-deps

  // A row acted on that the refresh no longer shows: say where it went, with a "Show it" (FH-201)
  const hiddenRow = useHiddenRowNotice(viewFilters?.chips, activeFilters);
  const [presetSearch, setPresetSearch] = useState(null);
  // A search that arrives with a navigation (e.g. Atom opening the area it just built) also shows in the
  // search box, so the narrowed list explains itself; it survives the view switch that navigation makes.
  const navSearchRef = useRef(null);
  useEffect(() => { // another tab starts with its own empty search
    const pending = navSearchRef.current;
    setPresetSearch(pending && pending.viewId === activeViewId ? { text: pending.text } : null);
    navSearchRef.current = null;
  }, [activeViewId]);
  useEffect(() => { hiddenRow.reset(); }, [activeViewId, activeFilters, data.searchQuery, data.advancedSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { hiddenRow.check('master', masterData); }, [masterData]); // eslint-disable-line react-hooks/exhaustive-deps
  const handleMasterRowAction = useCallback((action, row) => {
    const master = activeView?.master || {};
    hiddenRow.watch({
      source: 'master', row: row?.data || row, keyField: master.primaryKey,
      loadScript: master.loadScript, loadParams: master.loadParams, data: masterData,
    });
    actions.handleRowAction(action, row);
  }, [activeView, masterData, actions.handleRowAction]); // eslint-disable-line react-hooks/exhaustive-deps
  const showHiddenRow = useCallback((key) => {
    defaultsLiftedRef.current = true;
    data.setActiveFilters([]);
    data.setAdvancedSearchParams({});
    data.setSearchQuery(key);
    setPresetSearch({ text: key });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdvancedSearch = useCallback((params) => {
    data.setAdvancedSearchParams(params);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleAdvancedSearchClear = useCallback(() => {
    data.setAdvancedSearchParams({});
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Row click for accordion expand
  const handleRowClick = useCallback((columnId, value, rowData) => {
    const masterConfig = activeView?.master;
    let isLink = false;
    if (masterConfig?.columns) {
      const col = masterConfig.columns.find((c) => c.field === columnId);
      isLink = col && col.type === 'LINK';
    } else {
      const entity = masterConfig?.entity;
      const fields = fieldMetaCache[entity] || [];
      isLink = fields.some((f) => f.fieldName === columnId && f.fieldType === 'LINK');
    }

    if (isLink && resolvedDetails.length > 0) {
      const rowId = String(rowData[resolvedDetails[0].parentKey] || value);
      if (expandedRowId === rowId) {
        setExpandedRowId(null);
        setExpandedRowData(null);
      } else {
        setExpandedRowId(rowId);
        setExpandedRowData(rowData);
        setActiveDetailTab(0);
        if (!detailData[rowId]) loadDetailData(rowData);
      }
    }
  }, [activeView, expandedRowId, detailData, loadDetailData, fieldMetaCache, resolvedDetails, setExpandedRowId, setActiveDetailTab]);

  // After an in-place refresh, re-point the expanded row at its fresh data and reload its child grids
  useEffect(() => {
    if (!expandedRowId || !resolvedDetails.length || !Array.isArray(masterData)) return;
    const parentKey = resolvedDetails[0].parentKey;
    const fresh = masterData.find((r) => String(r[parentKey]) === expandedRowId);
    if (fresh) {
      setExpandedRowData(fresh);
      loadDetailData(fresh);
    }
  }, [masterData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build table data wrappers — apply the user's saved column config (if any)
  // on top of the metadata-driven column list.
  // A grid's columnVisibility hides columns whose visibility script returns false.
  const buildTable = useCallback((panelConfig, tableData, title) => {
    const table = buildTableData(panelConfig, tableData, title, activeViewId, expandedRowId, fieldMetaCache, viewActions, activeFilters, currentUser?.roles);
    const columns = applyColumnVisibility(table.columns, panelConfig?.columnVisibility, columnVisibleByScript);
    return { ...table, columns: applyColumnPrefs(columns, table.entityType) };
  }, [activeViewId, expandedRowId, fieldMetaCache, viewActions, activeFilters, currentUser, applyColumnPrefs, columnPrefsVersion, columnVisibleByScript]);

  const buildDetailTable = useCallback((panelConfig, tableData, title) => {
    const table = buildDetailTableData(panelConfig, tableData, title, activeViewId, fieldMetaCache, currentUser?.roles);
    const columns = applyColumnVisibility(table.columns, panelConfig?.columnVisibility, columnVisibleByScript);
    return { ...table, columns: applyColumnPrefs(columns, table.entityType) };
  }, [activeViewId, fieldMetaCache, currentUser, applyColumnPrefs, columnPrefsVersion, columnVisibleByScript]);

  // View type detection
  const rawActiveView = rawViews.find((v) => v.viewId === activeViewId);
  const hasLayout = rawActiveView?.layout?.rows?.length > 0;
  // a view with a layout draws its own drag lists (WorkspaceLayoutView); only a layout-less one is a whole-view list
  const isOrderableList = !hasLayout && activeView?.master?.type === 'orderable-list';
  const isFormView = activeView?.master?.type === 'form';
  // Namespaced so two workspaces reusing the same viewId (e.g. both having a
  // "dashboard" tab) don't collide on the same help content.
  const helpViewKey = activeView ? (workspaceCode ? `${workspaceCode}.${activeView.viewId}` : activeView.viewId) : null;
  const isGridView = !hasLayout && !isOrderableList && !isFormView && (activeView?.master?.type === 'grid' || activeView?.master?.loadScript);

  const filteredMasterData = masterData;
  const isSearching = Boolean((data.searchQuery || '').trim())
    || Object.values(data.advancedSearchParams || {}).some((v) => v !== '' && v != null);

  // Grid view renderer
  const renderGridView = () => {
    if (!activeView?.master) return null;

    if (masterLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
          <CircularProgress size={32} sx={{ color: '#22c55e' }} />
        </Box>
      );
    }

    const table = buildTable(activeView.master, filteredMasterData, activeView.label);
    if (data.masterTotal != null) {
      table.paging = {
        total: data.masterTotal,
        loading: data.masterLoadingMore,
        loadMore: data.loadMoreMaster,
        sort: data.sortMaster,
      };
    }

    // Filter detail tabs by showWhen condition against expanded row data
    const visibleDetails = resolvedDetails.filter(det => {
      if (!det.showWhen || !expandedRowData) return true;
      const { field, in: inValues, notIn } = det.showWhen;
      const val = expandedRowData[field];
      if (inValues && !inValues.includes(val)) return false;
      if (notIn && notIn.includes(val)) return false;
      return true;
    });
    const showDetail = expandedRowId && visibleDetails.length > 0;

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1 }}>
        {/* Master grid */}
        <Box sx={{ flex: showDetail ? undefined : 1, height: showDetail ? `${splitPercent}%` : undefined, overflow: 'auto', minHeight: 120, display: 'flex', flexDirection: 'column' }}>
          <TableRenderer
            table={table}
            fillHeight
            colors={colors}
            onRowAction={handleMasterRowAction}
            onBulkAction={(action, ids) => {
              const selectedData = (table.rows || []).filter((r) => ids.includes(r.rowId)).map((r) => r.data);
              const actionType = (action.actionType || action.type || '').toUpperCase();
              if (actionType === 'BULK_CONFIRM') {
                actions.handleRowAction(action, { selected_ids: ids, selected_rows: selectedData });
              } else if (actionType === 'FORM' || actionType === 'WIZARD') {
                actions.handleRowAction(action, { selected_ids: ids, selected_rows: selectedData });
              } else {
                actions.executeAction(action, null, { selected_ids: ids, selected_rows: selectedData });
              }
            }}
            onGlobalAction={actions.handleGlobalAction}
            onLinkClick={handleRowClick}
            onConfigureTable={handleConfigureTable}
            onOpenWorkspace={onOpenWorkspace}
          />

          {filteredMasterData.length === 0 && isSearching && (
            // A search that matches nothing is not an empty queue: never say "all done" for it (FH-81)
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }} data-testid="search-no-match">
              <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                Nothing matches your search
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1.5 }}>
                {(data.searchQuery || '').trim() ? `No rows match "${data.searchQuery.trim()}".` : 'No rows match the search.'}
              </Typography>
              <Button size="small" variant="outlined" onClick={() => { handleStructuredSearchClear(); setPresetSearch({ text: '' }); }}
                sx={{ textTransform: 'none' }}>
                Clear search
              </Button>
            </Box>
          )}
          {filteredMasterData.length === 0 && !isSearching && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <Typography variant="h6" sx={{ color: colors.textSecondary, mb: 1 }}>
                {activeView.master.emptyState?.title || 'No data'}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {activeView.master.emptyState?.message || ''}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Draggable splitter */}
        {showDetail && (
          <Box
            onMouseDown={handleSplitDragStart}
            sx={{
              height: 6, cursor: 'row-resize', flexShrink: 0,
              bgcolor: colors.border,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              '&:hover': { bgcolor: '#22c55e' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box sx={{ width: 32, height: 2, bgcolor: colors.textMuted, borderRadius: 1 }} />
          </Box>
        )}

        {/* Detail grid */}
        {showDetail && (
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 120, border: `1px solid ${colors.border}`, borderTop: 'none' }}>
            {visibleDetails.length > 1 && (
              <Tabs
                value={activeDetailTab}
                onChange={(_, v) => setActiveDetailTab(v)}
                sx={{
                  minHeight: 32, bgcolor: colors.surfaceElevated,
                  borderBottom: `1px solid ${colors.border}`,
                  '& .MuiTab-root': { minHeight: 32, py: 0.5, px: 2, fontSize: '0.75rem', color: colors.textSecondary, textTransform: 'none' },
                  '& .Mui-selected': { color: '#22c55e' },
                  '& .MuiTabs-indicator': { backgroundColor: '#22c55e' },
                }}
              >
                {visibleDetails.map((det, idx) => (
                  <Tab key={det.entity || idx} label={entityDisplayName(det.entity) || det.label || det.entity || 'Detail'} value={idx} />
                ))}
              </Tabs>
            )}

            {(() => {
              const det = visibleDetails[activeDetailTab];
              if (!det) return null;
              const detKey = det.entity || det.loadScript;
              const rowDetailMap = detailData[expandedRowId];
              const detData = rowDetailMap ? rowDetailMap[detKey] : undefined;
              return (
                <AccordionDetail
                  parentKey={det.parentKey}
                  parentValue={expandedRowId}
                  detail={det}
                  detailData={detData}
                  detailLoading={detailLoading}
                  colors={colors}
                  buildDetailTableData={buildDetailTable}
                  masterData={masterData}
                  onRowAction={actions.handleRowAction}
                  onGlobalAction={actions.handleGlobalAction}
                  onBulkAction={(action, ids) => {
                    const selectedData = (detData || []).filter(r => ids.includes(String(r[det.primaryKey || 'id'] || r.inventoryId)));
                    const actionType = (action.actionType || action.type || '').toUpperCase();
                    if (actionType === 'BULK_CONFIRM' || actionType === 'FORM' || actionType === 'WIZARD') {
                      actions.handleRowAction(action, { selected_ids: ids, selected_rows: selectedData });
                    } else {
                      actions.executeAction(action, null, { selected_ids: ids, selected_rows: selectedData });
                    }
                  }}
                  onOpenWorkspace={onOpenWorkspace}
                  onConfigureTable={handleConfigureTable}
                />
              );
            })()}
          </Box>
        )}
      </Box>
    );
  };

  if (isCustomComponent) {
    if (!CustomComponent) {
      return (
        <Box sx={{ p: 3 }}>
          <Typography color="error">
            Unknown custom workspace component: {def.componentCode || '(none configured)'}
          </Typography>
        </Box>
      );
    }
    return <CustomComponent currentUser={currentUser} colors={colors} navContext={navContext} onOpenWorkspace={onOpenWorkspace} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: colors.background }}>
      {/* View tabs */}
      {views.length > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface }}>
          <Tabs
            // a hidden drill-down view (Zone Members, Workflow Questions) has no tab: none is selected while it shows
            value={views.some((v) => v.viewId === activeViewId) ? activeViewId : false}
            onChange={(_, v) => { setActiveViewId(v); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              flex: 1,
              minHeight: 36,
              '& .MuiTabs-indicator': { bgcolor: '#22c55e' },
              '& .MuiTab-root': {
                minHeight: 36, textTransform: 'none', color: colors.textSecondary, fontSize: '0.8rem',
                '&.Mui-selected': { color: '#22c55e' },
              },
            }}
          >
            {views.map((v) => (
              <Tab key={v.viewId} value={v.viewId} label={v.label} />
            ))}
          </Tabs>
          {/* Only shown here for non-form views -- form views render their own
              HelpIcon in the Reset toolbar row below, so avoid double icons. */}
          {!isFormView && activeView && (
            <Box sx={{ pr: 1 }}>
              <HelpIcon key={helpViewKey} viewKey={helpViewKey} title={activeView.label} colors={colors} />
            </Box>
          )}
        </Box>
      )}

      {/* Toolbar */}
      {isGridView && (
        <WorkspaceToolbar
          key={activeViewId /* a fresh search box per tab: typed text used to stay on screen without applying */}
          toolbar={viewToolbar}
          fieldMetadata={resolveSmartSearchFieldMeta(advancedSearchFieldMeta, activeView?.master, fieldMetaCache)}
          advancedSearchFieldMeta={advancedSearchFieldMeta}
          searchRows={Array.isArray(masterData) ? masterData : []}
          navParams={navParams}
          presetSearch={presetSearch}
          onSearch={handleStructuredSearch}
          onSearchClear={handleStructuredSearchClear}
          onAdvancedSearch={handleAdvancedSearch}
          onAdvancedSearchClear={handleAdvancedSearchClear}
          onRefresh={loadMasterData}
          onExport={handleExport}
          loading={masterLoading}
          colors={colors}
        />
      )}

      {/* Filter chips */}
      {isGridView && viewFilters && (
        <FilterChipBar
          filters={viewFilters}
          fieldMetadata={fieldMetaCache[activeView?.master?.entity] || []}
          activeFilters={activeFilters}
          onFilterChange={setActiveFilters}
          colors={colors}
        />
      )}
      {isGridView && <HiddenRowNotice notice={hiddenRow.notice} onShow={showHiddenRow} onClose={hiddenRow.dismiss} />}

      {/* Form view toolbar -- Reset discards in-progress edits and reloads
          the saved values from the DB via the same loadScript the view
          already uses to populate the form. */}
      {isFormView && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, borderBottom: `1px solid ${colors.border}` }}>
          <HelpIcon key={helpViewKey} viewKey={helpViewKey} title={activeView.label} colors={colors} />
          <Button
            size="small"
            onClick={() => { loadMasterData(); setFormResetNonce((n) => n + 1); }}
            disabled={masterLoading}
            sx={{ textTransform: 'none', fontSize: '0.75rem', color: colors.textSecondary, '&:hover': { color: '#22c55e' } }}
          >
            Reset
          </Button>
        </Box>
      )}

      {/* View content */}
      {isOrderableList && (
        <OrderableListPanel
          key={activeViewId}
          config={activeView.master}
          colors={colors}
          fieldMetaCache={fieldMetaCache[activeView.master?.entity] || {}}
          onError={handleActionError}
          onSaved={handleActionSuccess}
          refreshToken={layoutRefresh}
        />
      )}
      {isFormView && !masterLoading && !masterError && (
        <EntityWizardOverlay
          key={`${activeViewId}-${formResetNonce}`}
          entityName={activeView.master?.entity}
          rowData={masterData && masterData.length > 0 ? masterData[0] : (masterData && !Array.isArray(masterData) ? masterData : null)}
          scriptName={activeView.master?.saveScript}
          title={activeView.label || 'Settings'}
          saveLabel="Save Settings"
          colors={colors}
          onSave={(result) => { loadMasterData(); handleActionSuccess(savedMessage(result, 'Settings saved.')); }}
          onClose={() => {}}
          embedded={true}
        />
      )}
      {isFormView && masterLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 8 }}>
          <CircularProgress size={32} sx={{ color: '#22c55e' }} />
        </Box>
      )}
      {hasLayout && rawActiveView && (
        <WorkspaceLayoutView
          key={activeViewId}
          view={rawActiveView}
          refreshToken={layoutRefresh}
          colors={colors}
          fieldMetaCache={fieldMetaCache}
          onRowAction={actions.handleRowAction}
          onGlobalAction={actions.handleGlobalAction}
          onLinkClick={handleRowClick}
          onOpenWorkspace={onOpenWorkspace}
          onNavigateToView={setActiveViewId}
          onError={handleActionError}
          onSuccess={handleActionSuccess}
          onConfigureTable={handleConfigureTable}
          pendingNavParamsRef={pendingNavParamsRef}
          workspaceNavParams={navParams}
        />
      )}
      {masterError && <Alert severity="error" sx={{ m: 2 }}>{masterError}</Alert>}
      {isGridView && !masterError && renderGridView()}
      {!hasLayout && !isGridView && !isOrderableList && !isFormView && (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Typography variant="body2" sx={{ color: colors.textMuted }}>
            Dashboard view: {activeView?.label}
          </Typography>
        </Box>
      )}

      {/* Status bar */}
      {isGridView && <WorkspaceStatusBar counters={statusCounters} colors={colors} />}

      {/* Overlays */}
      <WorkspaceFormOverlay
        overlayMode={actions.overlayMode}
        overlayEntity={actions.overlayEntity}
        overlayRowData={actions.overlayRowData}
        onEditSave={actions.handleEditSave}
        onOverlayClose={actions.dismissOverlay}
        formOpen={actions.formOpen}
        formEntity={actions.formEntity}
        formDetailEntity={actions.formDetailEntity}
        formKey={actions.formKey}
        formRowData={actions.formRowData}
        formScriptName={actions.formScriptName}
        formTitle={actions.formTitle}
        formSaveLabel={actions.formSaveLabel}
        formForceCreate={actions.formForceCreate}
        onFormSave={actions.handleFormSave}
        onFormClose={actions.dismissForm}
        colors={colors}
      />

      <WorkspacePickerOverlay
        open={actions.pickerOpen}
        config={actions.pickerConfig}
        parentRow={actions.pickerParentRow}
        onSubmit={actions.handlePickerSubmit}
        onClose={actions.dismissPicker}
        colors={colors}
      />

      {/* Generic rapid-entry overlay (resolver phase + scan/submit loop). All
          domain-specific behavior comes from action.rapidEntry config in the workspace JSON. */}
      {actions.rapidEntryOpen && actions.rapidEntryConfig && (
        <RapidEntryOverlay
          config={actions.rapidEntryConfig}
          rowData={actions.rapidEntryRowData}
          colors={colors}
          onClose={actions.closeRapidEntry}
          onSuccess={handleActionSuccess}
        />
      )}

      {/* Criteria builder overlay */}
      {actions.criteriaBuilderOpen && actions.criteriaBuilderConfig && (
        <CriteriaBuilderPanel
          mode={actions.criteriaBuilderConfig.mode || (actions.criteriaBuilderRow ? 'edit' : 'create')}
          zoneData={actions.criteriaBuilderRow}
          config={actions.criteriaBuilderConfig}
          colors={colors}
          onSave={actions.handleCriteriaBuilderSave}
          onCancel={actions.dismissCriteriaBuilder}
        />
      )}

      {/* Count entry overlay */}
      {actions.countEntryOpen && actions.countEntryConfig && (
        <CountEntryPanel
          rowData={actions.countEntryRow}
          config={actions.countEntryConfig}
          colors={colors}
          onSave={actions.handleCountEntrySave}
          onCancel={actions.dismissCountEntry}
        />
      )}

      {/* Add or change a custom field (System Config, FH-205) */}
      <AddCustomFieldDialog
        open={!!actions.customFieldDialog}
        field={actions.customFieldDialog?.field}
        colors={colors}
        onCancel={actions.closeCustomFieldDialog}
        onAdded={actions.customFieldSaved}
      />

      {/* The photos of a record (RF gun or added here) */}
      <PhotosDialog target={actions.photosDialog} colors={colors} onClose={actions.closePhotosDialog} />

      {/* An inspection or work order, one step at a time (FH-364) */}
      <WizardRunPanel target={actions.wizardRunDialog} colors={colors} onClose={actions.closeWizardRunDialog} />

      {/* Bulk confirm overlay */}
      {actions.bulkConfirmOpen && (
        <BulkConfirmOverlay
          rows={actions.bulkConfirmRows}
          config={actions.bulkConfirmConfig}
          actionScript={actions.bulkConfirmScript}
          colors={colors}
          onClose={actions.dismissBulkConfirm}
          onComplete={actions.handleBulkConfirmComplete}
        />
      )}

      {/* Confirm dialog */}
      <WorkspaceConfirmDialog
        open={actions.confirmOpen}
        action={actions.confirmAction}
        onConfirm={actions.handleConfirm}
        onCancel={actions.handleConfirmCancel}
      />

      {/* Configure table columns dialog — shared by every grid on this workspace */}
      <ColumnConfigDialog
        open={columnConfigDialog.open}
        entityType={columnConfigDialog.entityType}
        colors={colors}
        onClose={() => setColumnConfigDialog({ open: false, entityType: null })}
        onSaved={(payload) => {
          // Update the cache directly so the grid re-renders with the new
          // columns immediately, instead of waiting on a refetch.
          columnPrefsCacheRef.current[columnConfigDialog.entityType] = payload;
          setColumnPrefsVersion((v) => v + 1);
          setErrorSnack({ severity: 'success', message: 'Column preferences saved.' });
        }}
      />

      {/* Action result dialog — generic renderer for any script result */}
      <Dialog open={!!actionResult} onClose={() => { setActionResult(null); setActionResultData(null); }} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: colors?.surface || colors?.cardBg, color: colors?.textPrimary } }}>
        <DialogTitle sx={{ fontWeight: 600, color: colors?.textPrimary }}>Result</DialogTitle>
        <DialogContent>
          {/* Summary text */}
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, mb: 2, color: colors?.textPrimary }}>
            {actionResult}
          </Typography>

          {/* Generic data renderer — renders any result object */}
          {actionResultData && Object.entries(actionResultData).map(([key, value]) => {
            // Skip the summary field (already shown above) and internal fields
            if (key === 'summary' || key === 'impactSummary' || key === 'content' || key === 'message' || key === 'success' || key.startsWith('_')) return null;

            // Arrays → render as a simple table
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
              const columns = Object.keys(value[0]).filter(k => !k.startsWith('_') && k !== 'tenantId' && k !== 'createdAt' && k !== 'updatedAt' && k !== 'id');
              return (
                <Box key={key} sx={{ mt: 2 }}>
                  <Typography variant="overline" sx={{ color: colors?.textMuted, display: 'block', mb: 0.5 }}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
                  </Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr>
                          {columns.map(col => (
                            <th key={col} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: `1px solid ${colors?.border || '#e2e8f0'}`, color: colors?.textMuted, fontWeight: 500 }}>
                              {col.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {value.map((row, i) => (
                          <tr key={i}>
                            {columns.map(col => (
                              <td key={col} style={{ padding: '4px 8px', borderBottom: `1px solid ${colors?.border || '#e2e8f0'}`, color: colors?.textPrimary }}>
                                {typeof row[col] === 'boolean' ? (row[col] ? 'Yes' : 'No') : String(row[col] ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              );
            }

            // Scalars → render as key: value
            if (value !== null && value !== undefined && typeof value !== 'object') {
              return (
                <Box key={key} sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                  <Typography variant="body2" sx={{ color: colors?.textMuted, minWidth: 120 }}>
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim()}:
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors?.textPrimary, fontWeight: 500 }}>
                    {String(value)}
                  </Typography>
                </Box>
              );
            }

            return null;
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionResult(null); setActionResultData(null); }} variant="contained" size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Success/error snackbar */}
      <Snackbar open={!!errorSnack} autoHideDuration={6000} onClose={() => setErrorSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {errorSnack && (
          <Alert onClose={() => setErrorSnack(null)} severity={errorSnack.severity} variant="filled" sx={{ width: '100%' }}>
            {errorSnack.message}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default MetadataWorkspace;
